import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Drawer } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DatabaseOutlined,
  LineChartOutlined,
  HistoryOutlined,
  MenuOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ROLE_IDS } from '@/domain/constants/roles';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { isSiteUser } from '@/features/auth/utils/authUtils';
import { canViewPurchaseOrderManagementLink } from '@/features/auth/utils/permissionUtils';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { logger } from '@/shared/lib/logger';
import { DARK_THEME } from '@/shared/constants/colors';
import { TypographyUtils } from '@/shared/constants/typography';
import { getAllPanels, type PanelBasicInfo } from '@/features/panels/services/panelApiService';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// レイアウト定数
const LAYOUT_CONSTANTS = {
  SIDER_WIDTH: 240,
  DRAWER_WIDTH: 280,
  HEADER_Z_INDEX: 1,
} as const;

/**
 * レイアウトスタイル定数
 *
 * Ant Design CSS-in-JSとの詳細度競合を防ぐためインラインスタイルで定義。
 * className（Tailwind）ではAnt Designのflex:autoやflex-direction等が
 * 上書きできないため、style属性で直接指定する必要がある。
 */
const LAYOUT_STYLES = {
  outerLayout: {
    height: '100dvh',
    overflow: 'hidden',
    flexDirection: 'row',
  } as React.CSSProperties,
  sider: {
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflow: 'auto',
  } as React.CSSProperties,
  innerLayout: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as React.CSSProperties,
  header: {
    flexShrink: 0,
    zIndex: LAYOUT_CONSTANTS.HEADER_Z_INDEX,
  } as React.CSSProperties,
  content: {
    overflow: 'auto',
    flex: '1 1 0',
  } as React.CSSProperties,
} as const;

// ルートパス → ページタイトルのマッピング
const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'ダッシュボード',
  '/panels': '運用詳細',
  '/realtime-data': 'リアルタイムデータ',
  '/history-data': '過去データ',
  '/inventory': '在庫管理',
  '/purchase-orders': '発注管理',
  '/settings': 'システム設定',
};

/**
 * 現在のパスからページタイトルを導出する
 */
const resolvePageTitle = (pathname: string): string => {
  // 完全一致を優先
  if (PAGE_TITLES[pathname] !== undefined) {
    return PAGE_TITLES[pathname];
  }
  // `/panels/:panelId` のようなパスはプレフィックスで一致
  const matchedKey = Object.keys(PAGE_TITLES).find((key) => pathname.startsWith(key));
  return matchedKey !== undefined ? PAGE_TITLES[matchedKey] : '';
};

export const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [availablePanels, setAvailablePanels] = useState<readonly PanelBasicInfo[]>([]);

  // ユーザーのアクセス可能な盤リストを取得
  useEffect(() => {
    const fetchAvailablePanels = async (): Promise<void> => {
      try {
        const panels = await getAllPanels();
        setAvailablePanels(panels);
      } catch (error) {
        logger.captureError(error, 'AppLayout.fetchAvailablePanels');
      }
    };

    if (user) {
      fetchAvailablePanels();
    }
  }, [user]);

  // デフォルトの盤ID（アクセス可能な最初の盤）
  const defaultPanelId = useMemo(() => {
    return availablePanels.length > 0 ? availablePanels[0].id : null;
  }, [availablePanels]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerVisible(false); // モバイルではメニュー選択後にDrawerを閉じる
    }
  };

  // 開発ユーザーまたはMCMユーザーかどうかをチェック
  const canAccessSettings = user?.roleId === ROLE_IDS.DEV_USER || user?.roleId === ROLE_IDS.MCM_USER;

  // 盤関連のメニューパスを動的に生成
  const panelDetailPath = defaultPanelId !== null ? `/panels/${defaultPanelId}` : '/dashboard';
  const realtimeDataPath = defaultPanelId !== null ? `/realtime-data?panelId=${defaultPanelId}` : '/realtime-data';
  const historyDataPath = defaultPanelId !== null ? `/history-data?panelId=${defaultPanelId}` : '/history-data';

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'ダッシュボード',
      onClick: () => handleMenuClick('/dashboard'),
    },
    {
      key: panelDetailPath,
      icon: <DatabaseOutlined />,
      label: '運用詳細',
      onClick: () => handleMenuClick(panelDetailPath),
    },
    {
      key: '/realtime-data',
      icon: <LineChartOutlined />,
      label: 'リアルタイムデータ',
      onClick: () => handleMenuClick(realtimeDataPath),
    },
    {
      key: '/history-data',
      icon: <HistoryOutlined />,
      label: '過去データ',
      onClick: () => handleMenuClick(historyDataPath),
    },
    {
      key: '/inventory',
      icon: <AppstoreOutlined />,
      label: '在庫管理',
      onClick: () => handleMenuClick('/inventory'),
    },
    // 発注管理リンクは SITE_VIEWER 以外に表示(設計書 07-permissions §6.1)、サイト境界はサーバー側で適用
    ...(canViewPurchaseOrderManagementLink(user) ? [{
      key: '/purchase-orders',
      icon: <ShoppingCartOutlined />,
      label: '発注管理',
      onClick: () => handleMenuClick('/purchase-orders'),
    }] : []),
    // 設定画面は開発ユーザーとMCMユーザーのみ表示
    ...(canAccessSettings ? [{
      key: '/settings',
      icon: <SettingOutlined />,
      label: '設定',
      onClick: () => handleMenuClick('/settings'),
    }] : []),
  ];

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: handleLogout,
    },
  ];

  const renderSideMenu = () => (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
    />
  );

  return (
    <Layout style={LAYOUT_STYLES.outerLayout}>
      {/* デスクトップ・タブレット用サイドバー（モバイル以外） */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="dark"
          width={LAYOUT_CONSTANTS.SIDER_WIDTH}
          style={LAYOUT_STYLES.sider}
        >
          <div className="h-16 flex items-center justify-center text-white text-xl font-bold">
            {collapsed ? 'FARO' : 'FARO System'}
          </div>
          {renderSideMenu()}
        </Sider>
      )}

      {/* モバイル用ドロワー */}
      {isMobile && (
        <Drawer
          title={
            <span className="text-white text-xl font-bold">
              FARO System
            </span>
          }
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={LAYOUT_CONSTANTS.DRAWER_WIDTH}
          styles={{
            header: {
              background: DARK_THEME.BACKGROUND,
              borderBottom: `1px solid ${DARK_THEME.BORDER}`
            },
            body: {
              padding: 0,
              background: DARK_THEME.BACKGROUND
            },
          }}
          closeIcon={<span className="text-white">✕</span>}
        >
          {renderSideMenu()}
        </Drawer>
      )}

      <Layout style={LAYOUT_STYLES.innerLayout}>
        <Header className="bg-white px-4 flex items-center justify-between shadow-sm"
          style={LAYOUT_STYLES.header}>
          {isMobile ? (
            // モバイル用ハンバーガーメニュー
            <button
              onClick={() => setDrawerVisible(!drawerVisible)}
              className="text-lg text-gray-400 hover:bg-gray-100 p-2 rounded border-none cursor-pointer"
            >
              <MenuOutlined className="scale-y-[0.8]" />
            </button>
          ) : (
            // デスクトップ・タブレット用展開/折りたたみボタン
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="text-lg text-gray-400 hover:bg-gray-100 p-2 rounded border-none cursor-pointer"
            >
              {collapsed ? <MenuUnfoldOutlined className="scale-y-[0.8]" /> : <MenuFoldOutlined className="scale-y-[0.8]" />}
            </button>
          )}

          <span style={TypographyUtils.getPresetStyle('SECTION_TITLE')}>
            {resolvePageTitle(location.pathname)}
            {location.pathname === '/dashboard' && isSiteUser(user) && user?.site && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                （{user.site.siteName}）
              </span>
            )}
          </span>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space className="cursor-pointer">
              <Avatar icon={<UserOutlined />} />
              {!isMobile && (
                <div className="flex flex-col">
                  <Text strong>{user?.userName}</Text>
                  <Text type="secondary" className="text-xs">
                    {user?.role?.roleName}
                    {user?.site && ` - ${user.site.siteName}`}
                  </Text>
                </div>
              )}
            </Space>
          </Dropdown>
        </Header>

        <Content className={isMobile ? "m-3 p-3 bg-white rounded-lg shadow-sm" : "m-6 p-6 bg-white rounded-lg shadow-sm"}
          style={LAYOUT_STYLES.content}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};